// src/features/master-data/hooks/useMasterData.js
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { masterDataApi } from '../services/masterDataApi';

export const useMasterData = (type, initialFilters = {}) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [filters, setFilters] = useState(initialFilters);
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await masterDataApi.getAll(type, {
        page: pagination.currentPage,
        per_page: pagination.perPage,
        sort_by: sortColumn,
        sort_dir: sortDirection,
        ...filters,
      });
      
      setData(response.data.data || response.data);
      setPagination({
        currentPage: response.data.meta.current_page || 1,
        totalPages: response.data.meta.last_page || 1,
        totalItems: response.data.meta.total || response.data.length,
        perPage: response.data.meta.per_page || pagination.perPage,
      });
    } catch (err) {
      setError(err.response?.data?.message || `Failed to fetch ${type}`);
      toast.error(`Failed to load ${type}`);
    } finally {
      setLoading(false);
    }
  }, [type, pagination.currentPage, pagination.perPage, sortColumn, sortDirection, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const create = async (data) => {
    try {
      const response = await masterDataApi.create(type, data);
      toast.success(`${type} created successfully`);
      fetchData();
      return response.data;
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to create ${type}`);
      throw err;
    }
  };

  const update = async (id, data) => {
    try {
      const response = await masterDataApi.update(type, id, data);
      toast.success(`${type} updated successfully`);
      fetchData();
      return response.data;
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to update ${type}`);
      throw err;
    }
  };

  const remove = async (id) => {
    try {
      await masterDataApi.delete(type, id);
      toast.success(`${type} deleted successfully`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to delete ${type}`);
      throw err;
    }
  };

  const bulkDelete = async (ids) => {
    try {
      await masterDataApi.bulkDelete(type, ids);
      toast.success(`${ids.length} ${type} deleted successfully`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to delete ${type}`);
      throw err;
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      const response = await masterDataApi.toggle_status(type, id);
      toast.success(`Status updated successfully`);
      fetchData();
      return response.data;
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to update status`);
      throw err;
    }
  };

  const importData = async (file) => {
    try {
      const response = await masterDataApi.import(type, file);
      toast.success(`Data imported successfully`);
      fetchData();
      return response.data;
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to import data`);
      throw err;
    }
  };

  const exportData = async (format = 'csv') => {
    try {
      const response = await masterDataApi.export(type, { format, ...filters });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}-${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Data exported successfully`);
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to export data`);
      throw err;
    }
  };

  const updateFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const resetFilters = () => {
    setFilters({});
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const changePage = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  const changePageSize = (size) => {
    setPagination(prev => ({ ...prev, perPage: size, currentPage: 1 }));
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getById = async (id) => {
    try {
      const response = await masterDataApi.getById(type, id);
      return response.data;
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to fetch ${type}`);
      throw err;
    }
  };

  return {
    data,
    loading,
    error,
    pagination,
    filters,
    sortColumn,
    sortDirection,
    fetchData,
    create,
    update,
    delete: remove,
    bulkDelete,
    toggleStatus,
    import: importData,
    export: exportData,
    updateFilters,
    resetFilters,
    changePage,
    changePageSize,
    handleSort,
    getById,
  };
};